import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { fetchCustomerOrders, type CustomerOrders } from "./api";

// File separato da api.ts (stesso schema di useOrderMargins.ts/economicSummaryApi.ts):
// un hook che chiama una funzione DELLO STESSO modulo non è mockabile nei test
// (vi.mock+importActual non intercetta le chiamate interne allo stesso file —
// limite noto Vitest/Jest). Da un file diverso, l'import di fetchCustomerOrders
// è cross-modulo e i test possono mockarlo davvero.

/** Hook per la CustomerSection: stesso pattern di useOrderDetail (AbortController,
 *  errore prettificato). Errore → data null (la sezione degrada in silenzio:
 *  la scheda cliente base non dipende dal 360). */
export function useCustomerOrders(orderRef: string | undefined): {
  data: CustomerOrders | null;
  isLoading: boolean;
  error: string | null;
} {
  const [data, setData] = useState<CustomerOrders | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderRef) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return undefined;
    }
    const ctrl = new AbortController();
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    // Passa il signal così una navigazione rapida annulla davvero la richiesta
    // in volo (bug-hunt 2026-07-13: prima l'AbortController era inerte).
    fetchCustomerOrders(orderRef, ctrl.signal)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled || ctrl.signal.aborted) return;
        setError(prettifyApiError(err));
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [orderRef]);

  return { data, isLoading, error };
}
